/*
 * water-bear.js: plays the LogCase tardigrade from water-bear.json on a <canvas>.
 *
 * No dependencies. Same rules as the iOS app: 10 fps, frames snap (never
 * interpolate), colours are the accent colour at a few opacities, sprites face left
 * and are mirrored to face right.
 *
 *   const data = await WaterBear.load('/static/water-bear/water-bear.json');
 *   const bear = WaterBear.mount(document.querySelector('#bear'), data, {
 *     animation: 'idle',   // 'idle' | 'walk' | 'tun' | 'revive' | 'reconnect'
 *     pixel: 4,            // CSS px per sprite pixel. 2 is the smallest that reads
 *     walkAcross: false,   // walk back and forth across the element's width
 *   });
 *   bear.play('walk');
 *   bear.destroy();
 *
 * Colours come from CSS custom properties on the element (or any ancestor):
 *   --wb-accent  (default #00518E, the brand navy)
 *   --wb-water   (default #2E8BC7)
 * Set them per theme, e.g. #E6E4A7 / #6FC6F0 in dark mode.
 */
(function (global) {
  'use strict';

  const FPS = 10;
  const WALK_PERIOD = 160; // ticks for one there-and-back walk, as in the app
  const DEFAULTS = { accent: '#00518E', water: '#2E8BC7' };

  async function load(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error('water-bear: ' + res.status + ' loading ' + url);
    return res.json();
  }

  function width(rows) {
    return rows.reduce((w, r) => Math.max(w, r.length), 0);
  }

  function mirrored(rows) {
    const w = width(rows);
    return rows.map((r) => r.padEnd(w, '.').split('').reverse().join(''));
  }

  /** Frame for `tick` in an animation; one-shot animations hold their last frame. */
  function frameAt(data, name, tick) {
    const a = data.animations[name];
    const duration = a.holds.reduce((s, h) => s + h, 0);
    let t = a.loops ? tick % duration : Math.min(tick, duration - 1);
    for (let i = 0; i < a.holds.length; i++) {
      if (t < a.holds[i]) return data.sprites[a.frames[i]];
      t -= a.holds[i];
    }
    return data.sprites[a.frames[a.frames.length - 1]];
  }

  /** Dry until the drop lands, the revival once, then idle. */
  function reconnectAt(data, tick) {
    const landing = data.sequences.reconnect.tunUntilTick;
    if (tick < landing) return frameAt(data, 'tun', tick);
    const t = tick - landing;
    const r = data.animations.revive;
    const duration = r.holds.reduce((s, h) => s + h, 0);
    return t < duration ? frameAt(data, 'revive', t) : frameAt(data, 'idle', t - duration);
  }

  function mount(el, data, options) {
    const opts = Object.assign({ animation: 'idle', pixel: 4, walkAcross: false }, options);
    const canvas = el.tagName === 'CANVAS' ? el : el.appendChild(document.createElement('canvas'));
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.imageRendering = 'pixelated';
    const ctx = canvas.getContext('2d');
    const spriteW = width(data.sprites.base);
    const spriteH = data.sprites.base.length;
    const reduceMotion = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let animation = opts.animation;
    let tick = 0;
    let timer = null;

    function colours() {
      const cs = getComputedStyle(canvas);
      const v = (name, fallback) => cs.getPropertyValue(name).trim() || fallback;
      return { accent: v('--wb-accent', DEFAULTS.accent), water: v('--wb-water', DEFAULTS.water) };
    }

    function size() {
      const p = opts.pixel;
      const cssW = opts.walkAcross ? Math.max(el.clientWidth || 0, spriteW * p) : spriteW * p;
      const cssH = spriteH * p;
      const dpr = global.devicePixelRatio || 1;
      canvas.style.width = cssW + 'px';
      canvas.style.height = cssH + 'px';
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return cssW;
    }

    function draw() {
      const p = opts.pixel;
      const cssW = size();
      let rows = animation === 'reconnect' ? reconnectAt(data, tick) : frameAt(data, animation, tick);
      let x0 = 0;
      if (opts.walkAcross && animation === 'walk') {
        // Triangle wave, facing the direction of travel: right to left, then back.
        const phase = (tick % WALK_PERIOD) / WALK_PERIOD;
        const facingLeft = phase < 0.5;
        const travel = facingLeft ? 1 - phase * 2 : phase * 2 - 1;
        x0 = Math.round(((cssW - spriteW * p) * travel) / p) * p;
        if (!facingLeft) rows = mirrored(rows);
      } else if (opts.facing === 'right') {
        rows = mirrored(rows);
      }

      const c = colours();
      ctx.clearRect(0, 0, cssW, spriteH * p);
      rows.forEach((row, y) => {
        for (let x = 0; x < row.length; x++) {
          const role = data.palette[row[x]];
          if (!role) continue;
          ctx.globalAlpha = role.opacity;
          ctx.fillStyle = role.role === 'water' ? c.water : c.accent;
          ctx.fillRect(x0 + x * p, y * p, p, p);
        }
      });
      ctx.globalAlpha = 1;
    }

    function start() {
      stop();
      draw();
      if (reduceMotion) return; // first frame only, no movement
      timer = setInterval(() => { tick += 1; draw(); }, 1000 / FPS);
    }

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    start();
    return {
      play(name) {
        if (!(name === 'reconnect' || data.animations[name])) throw new Error('water-bear: no animation ' + name);
        animation = name;
        tick = 0;
        start();
      },
      redraw: draw,
      stop,
      destroy() {
        stop();
        if (canvas !== el) canvas.remove();
      },
    };
  }

  /** Draws one static sprite (e.g. an effect) and returns the canvas. */
  function sprite(data, name, pixel, colour) {
    const rows = data.sprites[name];
    const canvas = document.createElement('canvas');
    const p = pixel || 4;
    canvas.width = width(rows) * p;
    canvas.height = rows.length * p;
    canvas.style.imageRendering = 'pixelated';
    const ctx = canvas.getContext('2d');
    rows.forEach((row, y) => {
      for (let x = 0; x < row.length; x++) {
        const role = data.palette[row[x]];
        if (!role) continue;
        ctx.globalAlpha = role.opacity;
        ctx.fillStyle = colour || DEFAULTS.accent;
        ctx.fillRect(x * p, y * p, p, p);
      }
    });
    return canvas;
  }

  global.WaterBear = { load, mount, sprite, frameAt };
})(window);
